import React, { useEffect, useState, useRef } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import FloatingDocumentChatFixed from "@/components/documents/chat/FloatingDocumentChatFixed";
import "@/styles/documentChat.css";

import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "./context/AuthContext";
import { AdminProvider } from "./context/AdminContext";
import { ChatProvider } from "./context/ChatContext";
import { TourProvider } from "./context/TourContext";
import { TourRunner } from "./components/tours/TourRunner";
import { FloatingButtonsContainer } from "./components/FloatingButtonsContainer";
import { FloatingAssessorIcon } from "./components/FloatingAssessor/FloatingAssessorIcon";
import { DocumentChatProvider } from "./context/DocumentChatContext";
import AnnouncementBanner from "./components/AnnouncementBanner";
import ScrollToTop from "./components/ScrollToTop";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import Index from "./pages/Index";
import Dashboard from "./pages/Dashboard";
import Chat from "./pages/Chat";
import Documents from "./pages/Documents";
import Settings from "./pages/Settings";
import PdfSettings from "./pages/PdfSettings";
import Plans from "./pages/Plans";
import ImproveText from "./pages/ImproveText";
import NotFound from "./pages/NotFound";
import AdminPanel from "./pages/AdminPanel";
import ThemePage from "./pages/ThemePage";
import Legal from "./pages/Legal";
import Suggestions from "./pages/Suggestions";
import Notifications from "./pages/Notifications";
import Calculos from "./pages/Calculos";
import Documentation from "./pages/Documentation";
import ChatDocs from "./pages/ChatDocs";
import ChatPlus from "./pages/ChatPlus";
import LegalChat from "./pages/LegalChat";
import Casos from "./pages/Casos";
import CasoDetail from "./pages/CasoDetail";
import Sharing from "./pages/Sharing";
import Jurisprudencias from "./pages/Jurisprudencias";
import Admin2FASetup from "./pages/Admin2FASetup";
import Admin2FAVerification from "./pages/Admin2FAVerification";
import AssessorPage from "./pages/Assessor";
import AgentConfig from "./pages/Assessor/AgentConfig";
import AssessorSettingsPage from "./pages/Assessor/Settings";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminProtectedRoute from "./components/AdminProtectedRoute";
import { NotificationContainer } from "@/components/NotificationContainer";
import { MobileWarningDialog } from "@/components/MobileWarningDialog";
import { supabase } from "./integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { DEBUG_CHAT, debugLog, isChatTarget, isEditorTarget } from "@/config/debug";
import ModernMobileMenu from "@/components/ui/modern-mobile-menu";
import MobileMenuPortal from "@/components/modern-mobile-menu/MobileMenuPortal";
import { usePageTracking } from "@/hooks/usePageTracking";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const FilesStorageInitializer = () => {
  const [bucketInitialized, setBucketInitialized] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const initializeFilesBucket = async () => {
      if (bucketInitialized) return;
      
      try {
        console.log("Storage: Checking files bucket...");
        
        const { data: listData, error: listError } = await supabase.storage
          .from('files')
          .list('', { limit: 1 });
          
        if (listError) {
          console.error("Storage: Cannot access 'files' bucket:", listError);
          
          if (!listError.message.includes("permission")) {
            try {
              console.log("Storage: Trying to create 'files' bucket...");
              const { error: createBucketError } = await supabase.storage.createBucket('files', {
                public: true,
                fileSizeLimit: 52428800,
                allowedMimeTypes: ['application/pdf', 'image/png', 'image/jpeg', 'application/msword', 'text/plain']
              });
              
              if (createBucketError) {
                if (createBucketError.message.includes("already exists")) {
                  console.log("Storage: Bucket 'files' already exists");
                  setBucketInitialized(true);
                } else {
                  console.error("Storage: Error creating bucket:", createBucketError);
                }
              } else {
                console.log("Storage: Bucket 'files' created successfully");
                setBucketInitialized(true);
              }
            } catch (error) {
              console.error("Storage: Error trying to create bucket:", error);
            }
          }
        } else {
          console.log("Storage: Bucket 'files' accessed successfully");
          setBucketInitialized(true);
        }
      } catch (error) {
        console.error("Storage: Error checking/initializing files bucket:", error);
      }
    };
    
    const checkAuthAndInit = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        initializeFilesBucket();
      }
    };
    
    checkAuthAndInit();
  }, [bucketInitialized, toast]);

  return null;
};

const SessionTableInitializer = () => {
  useEffect(() => {
    const initializeSessionTable = async () => {
      try {
        try {
          const { error: checkError } = await (supabase as any)
            .from('user_sessions')
            .select('id')
            .limit(1)
            .maybeSingle();
            
          if (!checkError) {
            console.log("Sessions: user_sessions table already exists");
            return;
          }
        } catch (err) {
          console.log("Sessions: Error checking for user_sessions table, it might not exist yet");
        }
        
        const { error: createError } = await supabase.rpc('create_user_sessions_table');
        
        if (createError) {
          console.error("Sessions: Error creating user_sessions table:", createError);
        } else {
          console.log("Sessions: user_sessions table created successfully");
        }
      } catch (error) {
        console.error("Sessions: Error initializing session table:", error);
      }
    };
    
    initializeSessionTable();
  }, []);
  
  return null;
};

// Componente para tracking de pageviews (precisa estar dentro do Router)
const PageTracker = () => {
  usePageTracking();
  return null;
};

const App = () => {
  const initializationRef = useRef(false);
  
  const runInitialization = () => {
    
    if (initializationRef.current) return;
    initializationRef.current = true;
    
    console.log("App: Initialized. Environment:", window.location.origin);
    
    // === ETAPA 1: INSTRUMENTAÇÃO GLOBAL DE EVENTOS (DEBUG) ===
    if (DEBUG_CHAT) {
      debugLog.window("🟢 SISTEMA DEBUG ATIVO - Instrumentando eventos globais");
      
      // 1) Tap global de eventos relevantes (em CAPTURA)
      const eventTypes = ['pointerdown','mousedown','click','beforeinput','input','keydown','keyup','focus','blur'];
      
      eventTypes.forEach(type => {
        document.addEventListener(type, (ev: Event) => {
          const target = ev.target as HTMLElement | null;
          const inChat = isChatTarget(ev);
          const inEditable = isEditorTarget(ev);
          const info = {
            type,
            inChat,
            inEditable,
            dp: (ev as any).defaultPrevented || false,
            phase: (ev as any).eventPhase, // 1=capture, 2=target, 3=bubble
            tag: target?.tagName || null,
            id: target?.getAttribute?.('id') || null,
            cls: target?.className || null
          };
          
          // Só loga eventos do chat ou quando algo impediu default no capture
          if (inChat || info.dp || info.type === 'beforeinput') {
            debugLog.event(info);
          }
        }, true); // true = fase de captura
      });

      // 2) Marcar focos/blur de janela
      window.addEventListener('blur', () => debugLog.window('window blur'));
      window.addEventListener('focus', () => debugLog.window('window focus'));
      
      // 3) Detectar múltiplas inicializações de Service Worker
      debugLog.serviceWorker(`sw_iframe init - ${performance.now()}`);
    }
    
    const urlParams = new URLSearchParams(window.location.search);
    const hasAuthParams = urlParams.has('code') || urlParams.has('token') || urlParams.has('error');
    const hasHash = window.location.hash && window.location.hash.includes('access_token');
    
    if (hasAuthParams) {
      console.log("App: Auth parameters detected in URL");
    }
    
    if (hasHash) {
      console.log("App: Auth hash detected");
    }
  };

  useEffect(() => {
    runInitialization();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <ThemeProvider 
          attribute="class" 
          defaultTheme="light" 
          enableSystem={false}
          disableTransitionOnChange={false}
        >
          <TooltipProvider>
            <BrowserRouter>
              <ScrollToTop />
              
              {/* ✅ MENU MOBILE - DENTRO do Router, FORA dos Providers com stacking context */}
              <MobileMenuPortal>
                <ModernMobileMenu />
              </MobileMenuPortal>
              
              {/* 📊 GTM Pageview Tracker */}
              <PageTracker />
              
                <AuthProvider>
                  <AdminProvider>
                    <ChatProvider>
                      <TourProvider>
                        <FilesStorageInitializer />
                        <SessionTableInitializer />
                        <TourRunner />
                        <div className="flex flex-col h-screen">
                          <AnnouncementBanner />
                          <div className="flex-1 min-h-0 overflow-auto relative" data-route-scroll-container>
                          <Routes>
                         <Route path="/" element={<Index />} />
                          <Route 
                            path="/painel" 
                            element={
                              <ProtectedRoute>
                                <SidebarProvider>
                                  <div className="absolute inset-0 z-10 flex min-h-0 w-full">
                                    <AppSidebar />
                                    <div className="flex-1 min-h-0 overflow-auto" data-route-scroll-container>
                                      <Dashboard />
                                    </div>
                                  </div>
                                </SidebarProvider>
                              </ProtectedRoute>
                            } 
                          />
                          <Route 
                            path="/chat" 
                            element={
                              <ProtectedRoute>
                                <SidebarProvider>
                                  <div className="absolute inset-0 z-10 flex min-h-0 w-full">
                                    <AppSidebar />
                                    <div className="flex-1 min-h-0 overflow-auto" data-route-scroll-container>
                                      <Chat />
                                    </div>
                                  </div>
                                </SidebarProvider>
                              </ProtectedRoute>
                            } 
                          />
                         <Route 
                           path="/chat/:conversationId" 
                           element={
                             <ProtectedRoute>
                               <SidebarProvider>
                                 <div className="absolute inset-0 z-10 flex min-h-0 w-full">
                                   <AppSidebar />
                                   <div className="flex-1 min-h-0 overflow-auto" data-route-scroll-container>
                                     <Chat />
                                   </div>
                                 </div>
                               </SidebarProvider>
                             </ProtectedRoute>
                           } 
                         />
                         <Route path="/chat-docs" element={<ChatDocs />} />
          <Route 
            path="/chat-plus" 
            element={
              <ProtectedRoute>
                <SidebarProvider>
                  <div className="absolute inset-0 z-10 flex min-h-0 w-full">
                    <AppSidebar />
                    <div className="flex-1 min-h-0 overflow-auto" data-route-scroll-container>
                      <ChatPlus />
                    </div>
                  </div>
                </SidebarProvider>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/chat-juridico" 
            element={
              <ProtectedRoute>
                <SidebarProvider>
                  <div className="absolute inset-0 z-10 flex min-h-0 w-full">
                    <AppSidebar />
                    <div className="flex-1 min-h-0 overflow-hidden">
                      <LegalChat />
                    </div>
                  </div>
                </SidebarProvider>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/chat-juridico/:conversationId" 
            element={
              <ProtectedRoute>
                <SidebarProvider>
                  <div className="absolute inset-0 z-10 flex min-h-0 w-full">
                    <AppSidebar />
                    <div className="flex-1 min-h-0 overflow-hidden">
                      <LegalChat />
                    </div>
                  </div>
                </SidebarProvider>
              </ProtectedRoute>
            } 
          />
                         <Route 
                           path="/jurisprudencias" 
                           element={
                             <ProtectedRoute>
                               <SidebarProvider>
                                 <div className="absolute inset-0 z-10 flex min-h-0 w-full">
                                   <AppSidebar />
                                   <div className="flex-1 min-h-0 overflow-auto" data-route-scroll-container>
                                     <Jurisprudencias />
                                   </div>
                                 </div>
                               </SidebarProvider>
                             </ProtectedRoute>
                           } 
                         />
                         <Route 
                           path="/casos" 
                           element={
                             <ProtectedRoute>
                               <SidebarProvider>
                                 <div className="absolute inset-0 z-10 flex min-h-0 w-full">
                                   <AppSidebar />
                                   <div className="flex-1 min-h-0 overflow-auto" data-route-scroll-container>
                                     <Casos />
                                   </div>
                                 </div>
                               </SidebarProvider>
                             </ProtectedRoute>
                           } 
                         />
                         <Route 
                           path="/casos/:id" 
                           element={
                             <ProtectedRoute>
                               <SidebarProvider>
                                 <div className="absolute inset-0 z-10 flex min-h-0 w-full">
                                   <AppSidebar />
                                   <div className="flex-1 min-h-0 overflow-auto" data-route-scroll-container>
                                     <CasoDetail />
                                   </div>
                                 </div>
                               </SidebarProvider>
                             </ProtectedRoute>
                           } 
                         />
                         <Route 
                           path="/compartilhamento" 
                           element={
                             <ProtectedRoute>
                               <SidebarProvider>
                                 <div className="absolute inset-0 z-10 flex min-h-0 w-full">
                                   <AppSidebar />
                                   <div className="flex-1 min-h-0 overflow-auto" data-route-scroll-container>
                                     <Sharing />
                                   </div>
                                 </div>
                               </SidebarProvider>
                             </ProtectedRoute>
                           } 
                         />
                             <Route
                              path="/documentos" 
                              element={
                               <ProtectedRoute>
                                 <DocumentChatProvider>
                                   <SidebarProvider>
                                     <div className="absolute inset-0 z-10 flex min-h-0 w-full">
                                       <AppSidebar />
                            <div className="flex-1 min-h-0 overflow-auto" data-route-scroll-container>
                              <Documents />
                            </div>
                                     </div>
                                   </SidebarProvider>
                                 </DocumentChatProvider>
                               </ProtectedRoute>
                             } 
                           />
                             <Route 
                               path="/documentos/:documentId" 
                               element={
                                <ProtectedRoute>
                                  <DocumentChatProvider>
                                     <SidebarProvider>
                                       <div className="absolute inset-0 z-10 flex min-h-0 w-full">
                                        <AppSidebar />
                                         <div className="flex-1 min-h-0 overflow-auto" data-route-scroll-container>
                                           <Documents />
                                         </div>
                                      </div>
                                    </SidebarProvider>
                                  </DocumentChatProvider>
                                </ProtectedRoute>
                              } 
                            />
                           <Route 
                             path="/melhorar-texto" 
                             element={
                              <ProtectedRoute>
                                <SidebarProvider>
                                  <div className="absolute inset-0 z-10 flex min-h-0 w-full">
                                    <AppSidebar />
                                    <div className="flex-1 min-h-0 overflow-auto" data-route-scroll-container>
                                      <ImproveText />
                                    </div>
                                  </div>
                                </SidebarProvider>
                              </ProtectedRoute>
                            } 
                          />
                           <Route 
                             path="/configuracoes" 
                             element={
                              <ProtectedRoute>
                                <SidebarProvider>
                                  <div className="absolute inset-0 z-10 flex min-h-0 w-full">
                                    <AppSidebar />
                                    <div className="flex-1 min-h-0 overflow-auto" data-route-scroll-container>
                                      <Settings />
                                    </div>
                                  </div>
                                </SidebarProvider>
                              </ProtectedRoute>
                            } 
                          />
                           <Route 
                             path="/papel-timbrado" 
                             element={
                              <ProtectedRoute>
                                <SidebarProvider>
                                  <div className="absolute inset-0 z-10 flex min-h-0 w-full">
                                    <AppSidebar />
                                    <div className="flex-1 min-h-0 overflow-auto" data-route-scroll-container>
                                      <PdfSettings />
                                    </div>
                                  </div>
                                </SidebarProvider>
                              </ProtectedRoute>
                            } 
                          />
                          <Route 
                            path="/planos" 
                            element={
                             <ProtectedRoute>
                               <SidebarProvider>
                                 <div className="absolute inset-0 z-10 flex min-h-0 w-full">
                                   <AppSidebar />
                                   <div className="flex-1 min-h-0 overflow-auto" data-route-scroll-container>
                                     <Plans />
                                   </div>
                                 </div>
                               </SidebarProvider>
                             </ProtectedRoute>
                           } 
                         />
             <Route 
               path="/tema" 
               element={
                <ProtectedRoute>
                  <SidebarProvider>
                    <div className="absolute inset-0 z-10 flex min-h-0 w-full">
                      <AppSidebar />
                      <div className="flex-1 min-h-0 overflow-auto" data-route-scroll-container>
                        <ThemePage />
                      </div>
                    </div>
                  </SidebarProvider>
                </ProtectedRoute>
              } 
            />
             <Route 
               path="/juridico" 
               element={
                <ProtectedRoute>
                  <SidebarProvider>
                    <div className="absolute inset-0 z-10 flex min-h-0 w-full">
                      <AppSidebar />
                      <div className="flex-1 min-h-0 overflow-auto" data-route-scroll-container>
                        <Legal />
                      </div>
                    </div>
                  </SidebarProvider>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/calculos" 
              element={
                <ProtectedRoute>
                  <SidebarProvider>
                    <div className="absolute inset-0 z-10 flex min-h-0 w-full">
                      <AppSidebar />
                      <div className="flex-1 min-h-0 overflow-auto" data-route-scroll-container>
                        <Calculos />
                      </div>
                    </div>
                  </SidebarProvider>
                </ProtectedRoute>
              } 
            />
                           <Route 
                             path="/notificacoes" 
                             element={
                              <ProtectedRoute>
                                <SidebarProvider>
                                  <div className="absolute inset-0 z-10 flex min-h-0 w-full">
                                    <AppSidebar />
                                    <div className="flex-1 min-h-0 overflow-auto" data-route-scroll-container>
                                      <Notifications />
                                    </div>
                                  </div>
                                </SidebarProvider>
                              </ProtectedRoute>
                            } 
                          />
                            <Route 
                              path="/sugestoes" 
                              element={
                               <ProtectedRoute>
                                 <SidebarProvider>
                                   <div className="absolute inset-0 z-10 flex min-h-0 w-full">
                                     <AppSidebar />
                                     <div className="flex-1 min-h-0 overflow-auto" data-route-scroll-container>
                                       <Suggestions />
                                     </div>
                                   </div>
                                 </SidebarProvider>
                               </ProtectedRoute>
                             } 
                           />
                           <Route 
                             path="/documentacao" 
                             element={
                               <ProtectedRoute>
                                 <SidebarProvider>
                                   <div className="absolute inset-0 z-10 flex min-h-0 w-full">
                                     <AppSidebar />
                                     <div className="flex-1 min-h-0 overflow-auto" data-route-scroll-container>
                                       <Documentation />
                                     </div>
                                   </div>
                                </SidebarProvider>
                                </ProtectedRoute>
                             }
                           />
                           <Route path="/docs" element={<Documentation />} />
                           <Route path="/assessor" element={
                             <ProtectedRoute>
                               <SidebarProvider>
                                  <div className="absolute inset-0 z-10 flex min-h-0 w-full">
                                    <AppSidebar />
                                    <div className="flex-1 min-h-0 overflow-hidden">
                                      <AssessorPage />
                                    </div>
                                 </div>
                               </SidebarProvider>
                             </ProtectedRoute>
                           } />
                           <Route path="/assessor/config/:agentId" element={
                             <ProtectedRoute>
                               <SidebarProvider>
                                 <div className="absolute inset-0 z-10 flex min-h-0 w-full">
                                   <AppSidebar />
                                   <div className="flex-1 min-h-0 overflow-auto" data-route-scroll-container>
                                     <AgentConfig />
                                   </div>
                                 </div>
                               </SidebarProvider>
                             </ProtectedRoute>
                           } />
                           <Route path="/configuracoes/assessor" element={<ProtectedRoute><AssessorSettingsPage /></ProtectedRoute>}
                           />
                           <Route 
                             path="/paineladmin" 
                             element={
                               <ProtectedRoute>
                                 <AdminProtectedRoute>
                                   <AdminPanel />
                                 </AdminProtectedRoute>
                               </ProtectedRoute>
                             } 
                           />
                           <Route 
                             path="/admin-2fa-setup" 
                             element={
                               <ProtectedRoute>
                                 <Admin2FASetup />
                               </ProtectedRoute>
                             } 
                           />
                           <Route 
                             path="/admin-2fa-verify" 
                             element={
                               <ProtectedRoute>
                                 <Admin2FAVerification />
                               </ProtectedRoute>
                             } 
                           />
                        <Route 
                          path="/pagamento-sucesso" 
                          element={
                            <ProtectedRoute>
                              <PaymentSuccess />
                            </ProtectedRoute>
                          } 
                        />
                        <Route 
                          path="/pagamento-cancelado" 
                          element={
                            <ProtectedRoute>
                              <PaymentCanceled />
                            </ProtectedRoute>
                          } 
                        />
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                          </div>
                        </div>
                           <Toaster />
                          <Sonner />
                          <NotificationContainer />
                          <MobileWarningDialog />
                          <FloatingButtonsContainer />
                          <FloatingAssessorIcon />
                        </TourProvider>
                      </ChatProvider>
                    </AdminProvider>
                </AuthProvider>
            </BrowserRouter>
          </TooltipProvider>
        </ThemeProvider>
      </HelmetProvider>
    </QueryClientProvider>
  );
};

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  useEffect(() => {
    toast({
      title: "Pagamento confirmado!",
      description: "Sua assinatura foi ativada com sucesso.",
      variant: "default",
    });
    const refreshSubscription = async () => {
      try {
        await supabase.functions.invoke('check-subscription');
      } catch (error) {
        console.error('Erro ao atualizar status da assinatura:', error);
      }
    };
    
    refreshSubscription();
  }, [toast]);
  
  return (
    <div className="container max-w-4xl mx-auto py-16 px-4 text-center">
      <h1 className="text-3xl font-bold mb-6">Pagamento Realizado com Sucesso!</h1>
      <p className="text-xl mb-8">Sua assinatura foi ativada e você já tem acesso a todos os benefícios do plano.</p>
      <Button onClick={() => navigate('/configuracoes')}>
        Ver detalhes da assinatura
      </Button>
    </div>
  );
};

const PaymentCanceled = () => {
  const navigate = useNavigate();
  
  return (
    <div className="container max-w-4xl mx-auto py-16 px-4 text-center">
      <h1 className="text-3xl font-bold mb-6">Pagamento Cancelado</h1>
      <p className="text-xl mb-8">Seu processo de pagamento foi cancelado. Nenhuma cobrança foi realizada.</p>
      <div className="space-x-4">
        <Button variant="outline" onClick={() => navigate('/casos')}>
          Voltar aos Casos
        </Button>
        <Button onClick={() => navigate('/planos')}>
          Ver planos novamente
        </Button>
      </div>
    </div>
  );
};

export default App;
